/**
 * SingleImageRestoreVizPanel — Color / Area view for a saved single-image
 * colony-count analysis.
 *
 * Layout:
 *   Left  : interactive chart (HSV/LAB scatter for Color view, area
 *           distribution for Area view) — clicking a point highlights the
 *           matching colony on the image.
 *   Right : source image with mask overlays per object; checklist below
 *           toggles each object's visibility on both the image AND the chart.
 *
 * Toolbar: Download Objects (zip of cropped mask PNGs), Download data CSV,
 *          Download data XLSX.
 *
 * The legacy line-chart / Objects-tab path doesn't apply in single-image
 * mode — there's only one frame, so we render scatter + image instead.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, FormControl, FormControlLabel, IconButton,
  InputLabel, MenuItem, Select, Switch, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import Plot from '../utils/plotlyBundle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DownloadIcon from '@mui/icons-material/Download';
import { normalizeColonyLab } from '../utils/colonyLab';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';

// Build an area-rank-keyed color map: smallest colony → cool end of the
// spectrum, largest → warm end. Hue sweeps 240° (blue) down to 0° (red)
// with fixed saturation/lightness so every colony reads as visually
// distinct. Returns { [obj_id]: [r, g, b] } for the colonies provided.
//
// This replaces the old fixed 10-colour palette that wrapped on
// `id % 10` — two colonies could share a colour and the visual mapping
// to the area chart didn't convey magnitude.
const FALLBACK_COLOR = [144, 202, 249];
function hslToRgb(h, s, l) {
  // h ∈ [0, 360), s/l ∈ [0, 1]
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}
function buildAreaColorMap(colonies) {
  const sorted = [...colonies].sort(
    (a, b) => (a.area ?? 0) - (b.area ?? 0),
  );
  const n = sorted.length;
  const map = Object.create(null);
  sorted.forEach((c, i) => {
    // 240° (blue, small) → 0° (red, large). 240/(N-1) per step.
    const hue = n <= 1 ? 240 : 240 - 240 * (i / (n - 1));
    map[c.obj_id] = hslToRgb(hue, 0.78, 0.55);
  });
  return map;
}

// ── Saved phenotypic schema → display rows ─────────────────────────────────
// The backend writes ColonyMeasurement: { colony_id, area_pixels, circularity,
// eccentricity, mean_color_lab: [L, a, b], texture_* }. Derive HSV from LAB
// the same way ColonySummaryPanel does so the scatter shows real values.
function labToRgb([L, a, b]) {
  // CIE LAB -> XYZ (D65)
  let y = (L + 16) / 116;
  let x = a / 500 + y;
  let z = y - b / 200;
  const f = (t) => (t * t * t > 0.008856 ? t * t * t : (t - 16 / 116) / 7.787);
  x = 95.047 * f(x); y = 100.000 * f(y); z = 108.883 * f(z);
  x /= 100; y /= 100; z /= 100;
  let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
  let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
  let bl = x * 0.0557 + y * -0.2040 + z * 1.0570;
  const gamma = (u) => (u > 0.0031308 ? 1.055 * Math.pow(u, 1 / 2.4) - 0.055 : 12.92 * u);
  r = gamma(r); g = gamma(g); bl = gamma(bl);
  return [
    Math.max(0, Math.min(255, Math.round(r * 255))),
    Math.max(0, Math.min(255, Math.round(g * 255))),
    Math.max(0, Math.min(255, Math.round(bl * 255))),
  ];
}
function rgbToHsv([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  const d = mx - mn;
  let h = 0;
  if (d !== 0) {
    if (mx === r) h = ((g - b) / d) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = mx === 0 ? 0 : d / mx;
  return [h, s, mx];
}

// Canonical view-row for a saved colony. Tolerates both raw backend shape
// (colony_id / area_pixels / mean_color_lab) and the older live-fetch shape
// (id / area / hue / sat / val) so this panel works in either context.
function toRow(c) {
  const id = c.colony_id ?? c.obj_id ?? c.id;
  const area = c.area_pixels ?? c.area ?? 0;
  let hue = c.hue, sat = c.sat, val = c.val;
  let L = null, a = null, b = null;
  const lab = normalizeColonyLab(c.mean_color_lab || c.lab);
  if (lab) {
    [L, a, b] = lab;
  }
  if ((hue == null || sat == null || val == null) && lab) {
    const rgb = labToRgb(lab);
    const [h, s, v] = rgbToHsv(rgb);
    hue = h; sat = s * 100; val = v * 100;
  }
  return {
    obj_id: id,
    area,
    hue: hue ?? 0,
    sat: sat ?? 0,
    val: val ?? 0,
    L: L ?? 0,
    a: a ?? 0,
    b: b ?? 0,
    circularity: c.circularity ?? null,
  };
}

// ── COCO RLE decoder (column-major) → row-major Uint8Array ─────────────────
function decodeRLE(counts, size) {
  const [h, w] = size;
  const total = h * w;
  const col = new Uint8Array(total);
  let pos = 0;
  for (let i = 0; i < counts.length; i++) {
    const c = counts[i];
    if (i % 2 === 1) {
      for (let j = 0; j < c && pos + j < total; j++) col[pos + j] = 1;
    }
    pos += c;
  }
  const out = new Uint8Array(total);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      out[y * w + x] = col[x * h + y];
    }
  }
  return out;
}

// Bounding box of a row-major mask
function bboxOf(mask, w, h) {
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask[y * w + x]) {
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  return { x0, y0, x1: x1 + 1, y1: y1 + 1 };
}

// ── Component ──────────────────────────────────────────────────────────────
export default function SingleImageRestoreVizPanel({
  view,                      // 'color' | 'area'
  colonies: rawColonies,     // saved ColonyMeasurement[] from phenotypic/results.json
  rleData,                   // parsed rle_data/frame_000000.json
  frameImageUrl,             // presigned URL to the source image
  analysisName,
  objectMeta,                // optional {obj_id: {name, customColor, ...}} from object_meta.json
}) {
  // Resolve the user-given name for an object, falling back to "Object N"
  // when the analysis was saved before the meta sidecar landed or when a
  // colony was added after the rename pass.
  const nameFor = useCallback(
    (id) => objectMeta?.[id]?.name || objectMeta?.[String(id)]?.name || `Object ${id}`,
    [objectMeta],
  );
  // Normalize the saved schema once so the rest of the component reads
  // canonical fields (obj_id, area, hue, sat, val, L, a, b).
  const colonies = useMemo(
    () => (Array.isArray(rawColonies) ? rawColonies.map(toRow) : []),
    [rawColonies],
  );
  // Build the area-rank → spectrum color map once per colonies set. Same map
  // drives the mask overlay AND every chart trace so the visual mapping is
  // consistent end-to-end. Declared up here so the baseLayer useEffect (which
  // paints masks) and the chart traces both close over the same instance.
  const colorMap = useMemo(() => buildAreaColorMap(colonies), [colonies]);
  const colorFor = useCallback(
    (id) => colorMap[id] || FALLBACK_COLOR,
    [colorMap],
  );
  const [visibility, setVisibility] = useState({});   // {obj_id: boolean}
  const [highlightedId, setHighlightedId] = useState(null);
  const [colorSpace, setColorSpace] = useState('hsv'); // 'hsv' | 'lab'
  const [chartMode, setChartMode] = useState(view === 'color' ? 'hsv-scatter' : 'area-rank');
  const [logArea, setLogArea] = useState(false);
  const [showAllObjectsList, setShowAllObjectsList] = useState(false);

  // Sync chart default when the parent toggles between views.
  useEffect(() => {
    setChartMode(view === 'color' ? 'hsv-scatter' : 'area-rank');
  }, [view]);

  // Default everything visible on first hydrate.
  useEffect(() => {
    if (!colonies?.length) return;
    setVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      colonies.forEach((c) => {
        if (next[c.obj_id] === undefined) { next[c.obj_id] = true; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [colonies]);

  // Load source image once.
  const imgRef = useRef(null);
  const [imgEl, setImgEl] = useState(null);
  useEffect(() => {
    if (!frameImageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImgEl(img);
    img.onerror = () => setImgEl(null);
    img.src = frameImageUrl;
    imgRef.current = img;
    return () => { imgRef.current = null; };
  }, [frameImageUrl]);

  // Display resolution: cap the longest side so the main-thread overlay
  // loop fits inside a few frames. Earlier we ran the per-pixel loop at the
  // original (≥ 7 MP) resolution × 131 masks per change, which locked the
  // tab. With DISPLAY_MAX_SIDE=1024 we cap mask work to ~1 MP × 131 ≈ 130M
  // ops and only re-run when the visibility set changes (highlight is a
  // cheap pure-canvas redraw).
  const DISPLAY_MAX_SIDE = 1024;
  const displayDims = useMemo(() => {
    if (!imgEl) return null;
    const srcW = imgEl.naturalWidth || imgEl.width;
    const srcH = imgEl.naturalHeight || imgEl.height;
    if (!srcW || !srcH) return null;
    const scale = Math.min(1, DISPLAY_MAX_SIDE / Math.max(srcW, srcH));
    return {
      srcW, srcH, scale,
      dispW: Math.max(1, Math.round(srcW * scale)),
      dispH: Math.max(1, Math.round(srcH * scale)),
    };
  }, [imgEl]);

  // Decode each mask once at the *display* resolution into a tiny Uint8Array.
  // Storing every entry is N × dispW × dispH bytes; for 1024² × 200 colonies
  // that's ~200 MB worst case — too much. Instead we store only the bbox-
  // cropped mask + its offset, which is typically <1% of the full image.
  const [decodedMasks, setDecodedMasks] = useState({});
  const [decoding, setDecoding] = useState(false);
  useEffect(() => {
    if (!rleData || !rleData.objects || !displayDims) {
      setDecodedMasks({});
      return undefined;
    }
    let cancelled = false;
    setDecoding(true);
    // Defer to a microtask so the UI can paint the "buffering" state before
    // we block on the decode loop.
    const tid = setTimeout(() => {
      const out = {};
      const { scale } = displayDims;
      for (const o of rleData.objects) {
        if (cancelled) return;
        try {
          const fullMask = decodeRLE(o.rle.counts, o.rle.size);
          const srcH = o.rle.size[0];
          const srcW = o.rle.size[1];
          const bb = bboxOf(fullMask, srcW, srcH);
          if (!bb) continue;
          // Downsample the bbox region by nearest-neighbour at `scale`.
          const dx0 = Math.floor(bb.x0 * scale);
          const dy0 = Math.floor(bb.y0 * scale);
          const dx1 = Math.ceil(bb.x1 * scale);
          const dy1 = Math.ceil(bb.y1 * scale);
          const cw = Math.max(1, dx1 - dx0);
          const ch = Math.max(1, dy1 - dy0);
          const small = new Uint8Array(cw * ch);
          const invScale = 1 / scale;
          for (let y = 0; y < ch; y++) {
            const sy = Math.min(srcH - 1, Math.floor((dy0 + y) * invScale));
            const syO = sy * srcW;
            for (let x = 0; x < cw; x++) {
              const sx = Math.min(srcW - 1, Math.floor((dx0 + x) * invScale));
              if (fullMask[syO + sx]) small[y * cw + x] = 1;
            }
          }
          out[o.obj_id] = { mask: small, w: cw, h: ch, x: dx0, y: dy0 };
        } catch { /* skip malformed */ }
      }
      if (!cancelled) {
        setDecodedMasks(out);
        setDecoding(false);
      }
    }, 0);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [rleData, displayDims]);

  // ── Pre-rendered base layer: image + colored mask fills for every object.
  // Recomputed only when the mask set or visibility changes — NOT on hover.
  const baseLayerRef = useRef(null);
  useEffect(() => {
    if (!imgEl || !displayDims) { baseLayerRef.current = null; return; }
    const { dispW, dispH } = displayDims;
    const base = document.createElement('canvas');
    base.width = dispW; base.height = dispH;
    const bctx = base.getContext('2d');
    bctx.drawImage(imgEl, 0, 0, dispW, dispH);

    const overlay = bctx.createImageData(dispW, dispH);
    for (let i = 3; i < overlay.data.length; i += 4) overlay.data[i] = 0;
    for (const c of colonies) {
      if (!visibility[c.obj_id]) continue;
      const m = decodedMasks[c.obj_id];
      if (!m) continue;
      const [r, g, b] = colorFor(c.obj_id);
      const a = 90;
      for (let y = 0; y < m.h; y++) {
        const dy = m.y + y;
        if (dy < 0 || dy >= dispH) continue;
        const yoOverlay = dy * dispW * 4;
        const yoMask = y * m.w;
        for (let x = 0; x < m.w; x++) {
          if (!m.mask[yoMask + x]) continue;
          const dx = m.x + x;
          if (dx < 0 || dx >= dispW) continue;
          const oi = yoOverlay + dx * 4;
          overlay.data[oi] = r;
          overlay.data[oi + 1] = g;
          overlay.data[oi + 2] = b;
          overlay.data[oi + 3] = a;
        }
      }
    }
    const buf = document.createElement('canvas');
    buf.width = dispW; buf.height = dispH;
    buf.getContext('2d').putImageData(overlay, 0, 0);
    bctx.drawImage(buf, 0, 0);

    baseLayerRef.current = base;
  }, [imgEl, displayDims, decodedMasks, visibility, colonies, colorFor]);

  // ── Live canvas: blit base layer + draw highlight bbox on top.
  // Highlight changes redraw only this composite, not the full overlay.
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !displayDims) return;
    const { dispW, dispH } = displayDims;
    canvas.width = dispW;
    canvas.height = dispH;
    const ctx = canvas.getContext('2d');
    if (baseLayerRef.current) {
      ctx.drawImage(baseLayerRef.current, 0, 0);
    } else {
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, dispW, dispH);
    }
    if (highlightedId != null) {
      const m = decodedMasks[highlightedId];
      if (m) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = Math.max(2, Math.round(Math.min(dispW, dispH) / 250));
        ctx.strokeRect(m.x, m.y, m.w, m.h);
      }
    }
  }, [displayDims, decodedMasks, highlightedId, baseLayerRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chart data shaping ────────────────────────────────────────────────────
  const visibleColonies = useMemo(
    () => colonies.filter((c) => visibility[c.obj_id]),
    [colonies, visibility],
  );

  // ── Plotly data + layout — shared dark theme + interactive zoom/pan/reset.
  const rgbCss = (id) => {
    const [r, g, b] = colorFor(id);
    return `rgb(${r},${g},${b})`;
  };
  const labelFor = (c) => `${nameFor(c.obj_id)}<br>area ${c.area} px`;
  const PLOT_CONFIG = {
    responsive: true, scrollZoom: true, displaylogo: false,
    displayModeBar: true,
    // Keep the toolbar minimal — zoom / pan / reset / autoscale / save PNG.
    modeBarButtonsToRemove: ['select2d', 'lasso2d', 'toggleSpikelines'],
  };
  const PLOT_STYLE = { width: '100%', height: '100%' };
  const DARK_BG = '#1a1a1c';
  const AXIS_COLOR = '#bbb';
  const GRID_COLOR = '#333';

  // areaPoints must come before areaTrace and handlePlotHover — both close
  // over it. JS strict mode throws "Cannot access 'areaPoints' before
  // initialization" when the const-declared identifier is referenced
  // earlier in the same scope.
  const areaPoints = useMemo(() => {
    const sorted = [...visibleColonies].sort((a, b) => (a.area || 0) - (b.area || 0));
    return sorted.map((c, i) => ({ id: c.obj_id, rank: i + 1, area: c.area || 0 }));
  }, [visibleColonies]);

  const areaTrace = useMemo(() => {
    const sortedVisible = areaPoints;
    return {
      type: 'scatter',
      mode: 'markers',
      x: sortedVisible.map((p) => p.rank),
      y: sortedVisible.map((p) => p.area),
      text: sortedVisible.map((p) => nameFor(p.id)),
      marker: {
        size: 9,
        color: sortedVisible.map((p) => rgbCss(p.id)),
        line: { color: '#222', width: 0.5 },
      },
      hovertemplate: '%{text}<br>rank %{x}<br>%{y} px<extra></extra>',
    };
  }, [areaPoints]);

  const areaLayout = useMemo(() => ({
    paper_bgcolor: DARK_BG, plot_bgcolor: DARK_BG,
    font: { color: AXIS_COLOR, size: 11 },
    showlegend: false,
    margin: { t: 10, r: 10, b: 50, l: 60 },
    dragmode: 'pan',
    xaxis: {
      title: { text: 'Colony rank (small → large)', font: { color: AXIS_COLOR } },
      gridcolor: GRID_COLOR, zerolinecolor: GRID_COLOR, color: AXIS_COLOR,
    },
    yaxis: {
      title: { text: 'Area (px)', font: { color: AXIS_COLOR } },
      type: logArea ? 'log' : 'linear',
      gridcolor: GRID_COLOR, zerolinecolor: GRID_COLOR, color: AXIS_COLOR,
    },
  }), [logArea]);

  // Plotly 3D axes prefer the object form for `title` in recent versions —
  // passing a bare string sometimes renders nothing in scatter3d. Also leave
  // `range` undefined when not supplied so Plotly auto-scales: CIELAB values
  // are typically clustered tightly (L≈70-95, a≈±15, b≈±30) and disappear
  // inside the theoretical [-128, 128] bounds at marker size 4.
  const axisCfg = (text, range) => ({
    title: { text, font: { color: AXIS_COLOR, size: 12 } },
    ...(range ? { range } : { autorange: true }),
    gridcolor: GRID_COLOR,
    zerolinecolor: GRID_COLOR,
    color: AXIS_COLOR,
    backgroundcolor: DARK_BG,
    showbackground: true,
    tickfont: { color: AXIS_COLOR, size: 10 },
  });
  const buildScene3D = (xTitle, yTitle, zTitle, xRange, yRange, zRange) => ({
    paper_bgcolor: DARK_BG, plot_bgcolor: DARK_BG,
    font: { color: AXIS_COLOR, size: 11 },
    showlegend: false,
    margin: { t: 0, r: 0, b: 0, l: 0 },
    scene: {
      bgcolor: DARK_BG,
      xaxis: axisCfg(xTitle, xRange),
      yaxis: axisCfg(yTitle, yRange),
      zaxis: axisCfg(zTitle, zRange),
      camera: { eye: { x: 1.5, y: 1.5, z: 1.2 } },
      aspectmode: 'cube',
    },
  });

  const hsv3DTrace = useMemo(() => ({
    type: 'scatter3d',
    mode: 'markers',
    x: visibleColonies.map((c) => c.hue),
    y: visibleColonies.map((c) => c.sat),
    z: visibleColonies.map((c) => c.val),
    text: visibleColonies.map(labelFor),
    marker: {
      size: 6,
      color: visibleColonies.map((c) => rgbCss(c.obj_id)),
      opacity: 0.95,
      line: { width: 0 },
    },
    hovertemplate: '%{text}<br>H %{x:.0f}°<br>S %{y:.0f}%<br>V %{z:.0f}%<extra></extra>',
  }), [visibleColonies]);

  const lab3DTrace = useMemo(() => ({
    type: 'scatter3d',
    mode: 'markers',
    x: visibleColonies.map((c) => c.L),
    y: visibleColonies.map((c) => c.a),
    z: visibleColonies.map((c) => c.b),
    text: visibleColonies.map(labelFor),
    marker: {
      size: 6,
      color: visibleColonies.map((c) => rgbCss(c.obj_id)),
      opacity: 0.95,
      line: { width: 0 },
    },
    hovertemplate: '%{text}<br>L %{x:.1f}<br>a* %{y:.1f}<br>b* %{z:.1f}<extra></extra>',
  }), [visibleColonies]);

  const hsvLayout = useMemo(
    () => buildScene3D('Hue (°)', 'Saturation (%)', 'Value (%)', [0, 360], [0, 100], [0, 100]),
    [],
  );
  // Let LAB autoscale; clusters disappear inside the theoretical [-128, 128]
  // bounds. The actual data range varies by sample.
  const labLayout = useMemo(
    () => buildScene3D('L* (lightness)', 'a* (green ⇄ red)', 'b* (blue ⇄ yellow)'),
    [],
  );

  // Hover the corresponding mask on the right canvas when the user hovers a
  // chart point. Plotly emits the point index relative to the trace; we map
  // back to the obj_id via the visibility-filtered list.
  const handlePlotHover = useCallback((ev) => {
    const idx = ev?.points?.[0]?.pointIndex;
    if (idx == null) return;
    const ids = view === 'area'
      ? areaPoints.map((p) => p.id)
      : visibleColonies.map((c) => c.obj_id);
    const id = ids[idx];
    if (id != null) setHighlightedId(id);
  }, [view, areaPoints, visibleColonies]);
  const handlePlotUnhover = useCallback(() => setHighlightedId(null), []);

  // ── Download Objects ZIP ─────────────────────────────────────────────────
  // Decodes masks at FULL source resolution on-demand so the saved PNGs are
  // usable for reports / training sets. Yields back to the event loop between
  // objects so the UI doesn't freeze on dense plates.
  const [downloading, setDownloading] = useState(false);
  const handleDownloadObjects = useCallback(async () => {
    if (!imgEl || !colonies.length || !rleData?.objects) return;
    setDownloading(true);
    try {
      const zip = new JSZip();
      const srcW = imgEl.naturalWidth, srcH = imgEl.naturalHeight;
      const srcCanvas = document.createElement('canvas');
      srcCanvas.width = srcW; srcCanvas.height = srcH;
      const srcCtx = srcCanvas.getContext('2d');
      srcCtx.drawImage(imgEl, 0, 0);

      const objMap = new Map(rleData.objects.map((o) => [o.obj_id, o]));
      let i = 0;
      for (const c of colonies) {
        const o = objMap.get(c.obj_id);
        if (!o) continue;
        const fullMask = decodeRLE(o.rle.counts, o.rle.size);
        const mH = o.rle.size[0];
        const mW = o.rle.size[1];
        const bb = bboxOf(fullMask, mW, mH);
        if (!bb) continue;
        const cw = bb.x1 - bb.x0;
        const ch = bb.y1 - bb.y0;
        const src = srcCtx.getImageData(bb.x0, bb.y0, cw, ch);
        for (let y = 0; y < ch; y++) {
          for (let x = 0; x < cw; x++) {
            const mi = (y + bb.y0) * mW + (x + bb.x0);
            if (!fullMask[mi]) src.data[(y * cw + x) * 4 + 3] = 0;
          }
        }
        const out = document.createElement('canvas');
        out.width = cw; out.height = ch;
        out.getContext('2d').putImageData(src, 0, 0);
        const blob = await new Promise((res) => out.toBlob(res, 'image/png'));
        if (blob) zip.file(`obj_${String(c.obj_id).padStart(4, '0')}.png`, blob);
        i += 1;
        if (i % 10 === 0) {
          // Let the UI breathe so the user sees "Preparing..." advancing.
          await new Promise((r) => setTimeout(r, 0));
        }
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${analysisName || 'objects'}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }, [imgEl, colonies, rleData, analysisName]);

  // ── Download data (CSV / XLSX) ───────────────────────────────────────────
  const dataRows = useMemo(() => colonies.map((c) => ({
    obj_id: c.obj_id,
    area_px: c.area ?? '',
    circularity: c.circularity ?? '',
    hue_deg: c.hue ?? '',
    saturation: c.sat ?? '',
    value: c.val ?? '',
    L: c.L ?? '',
    a: c.a ?? '',
    b: c.b ?? '',
  })), [colonies]);

  const downloadCSV = () => {
    const cols = Object.keys(dataRows[0] || { obj_id: '' });
    const csv = [cols.join(',')]
      .concat(dataRows.map((r) => cols.map((c) => r[c] ?? '').join(',')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${analysisName || 'colonies'}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const downloadXLSX = () => {
    const ws = XLSX.utils.json_to_sheet(dataRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'colonies');
    XLSX.writeFile(wb, `${analysisName || 'colonies'}.xlsx`);
  };

  // ── Object list (toggleable visibility) ──────────────────────────────────
  const ObjectList = () => {
    const visibleCount = colonies.filter((c) => visibility[c.obj_id]).length;
    const allOn = visibleCount === colonies.length;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1, maxHeight: 180, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Button
            size="small" variant="outlined"
            onClick={() => {
              const target = !allOn;
              const next = {};
              colonies.forEach((c) => { next[c.obj_id] = target; });
              setVisibility(next);
            }}
          >
            {allOn ? 'Hide all' : 'Show all'}
          </Button>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {visibleCount}/{colonies.length} visible
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setShowAllObjectsList((v) => !v)}>
            {showAllObjectsList ? 'Collapse' : 'Expand list'}
          </Button>
        </Box>
        {showAllObjectsList && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 0.25 }}>
            {colonies.map((c) => {
              const [r, g, b] = colorFor(c.obj_id);
              const on = visibility[c.obj_id];
              return (
                <Box
                  key={c.obj_id}
                  onMouseEnter={() => setHighlightedId(c.obj_id)}
                  onMouseLeave={() => setHighlightedId(null)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    px: 0.5, py: 0.25, borderRadius: 0.5,
                    bgcolor: c.obj_id === highlightedId ? '#1976d233' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onClick={() => setVisibility((p) => ({ ...p, [c.obj_id]: !p[c.obj_id] }))}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: `rgb(${r},${g},${b})`, opacity: on ? 1 : 0.3 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1, color: on ? '#ddd' : '#666', fontSize: '0.7rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                    title={`${nameFor(c.obj_id)} (#${c.obj_id})`}
                  >
                    {nameFor(c.obj_id)} · {c.area}px
                  </Typography>
                  {on ? <VisibilityIcon sx={{ fontSize: 14, color: '#90caf9' }} /> : <VisibilityOffIcon sx={{ fontSize: 14, color: '#555' }} />}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  // ── Chart selection (per view) ───────────────────────────────────────────
  const ColorChartControls = (
    <ToggleButtonGroup
      size="small" exclusive value={colorSpace}
      onChange={(_, v) => v && setColorSpace(v)}
      sx={{
        // The default MUI ToggleButton renders text in text.primary, which
        // disappears against this panel's dark background.
        '& .MuiToggleButton-root': {
          color: '#bbb',
          borderColor: '#444',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
        },
        '& .MuiToggleButton-root.Mui-selected': {
          color: '#fff',
          bgcolor: 'rgba(124,77,255,0.25)',
          '&:hover': { bgcolor: 'rgba(124,77,255,0.35)' },
        },
      }}
    >
      <ToggleButton value="hsv">HSV</ToggleButton>
      <ToggleButton value="lab">CIELAB</ToggleButton>
    </ToggleButtonGroup>
  );

  const AreaChartControls = (
    <FormControlLabel
      control={<Switch size="small" checked={logArea} onChange={(e) => setLogArea(e.target.checked)} />}
      label={<Typography variant="caption" sx={{ color: '#aaa' }}>Log scale</Typography>}
    />
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1, gap: 1 }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {view === 'color' ? ColorChartControls : AreaChartControls}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Download cropped PNG of each object as a zip">
          <span>
            <Button
              size="small" variant="outlined" startIcon={<DownloadIcon />}
              onClick={handleDownloadObjects} disabled={downloading || !imgEl || !colonies.length}
            >
              {downloading ? 'Preparing...' : 'Download Objects'}
            </Button>
          </span>
        </Tooltip>
        <Button size="small" variant="outlined" onClick={downloadCSV} disabled={!dataRows.length}>
          CSV
        </Button>
        <Button size="small" variant="outlined" onClick={downloadXLSX} disabled={!dataRows.length}>
          XLSX
        </Button>
      </Box>

      {/* Side-by-side: chart left, image right */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#1a1a1c', borderRadius: 1, p: 1 }}>
          {view === 'color' && colorSpace === 'hsv' && (
            <Plot
              data={[hsv3DTrace]}
              layout={hsvLayout}
              config={PLOT_CONFIG}
              style={PLOT_STYLE}
              useResizeHandler
              onHover={handlePlotHover}
              onUnhover={handlePlotUnhover}
            />
          )}
          {view === 'color' && colorSpace === 'lab' && (
            <Plot
              data={[lab3DTrace]}
              layout={labLayout}
              config={PLOT_CONFIG}
              style={PLOT_STYLE}
              useResizeHandler
              onHover={handlePlotHover}
              onUnhover={handlePlotUnhover}
            />
          )}
          {view === 'area' && (
            <Plot
              data={[areaTrace]}
              layout={areaLayout}
              config={PLOT_CONFIG}
              style={PLOT_STYLE}
              useResizeHandler
              onHover={handlePlotHover}
              onUnhover={handlePlotUnhover}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#0e0e10', borderRadius: 1, p: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {imgEl ? (
              <canvas
                ref={canvasRef}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="caption" sx={{ color: '#666' }}>
                Loading source image…
              </Typography>
            )}
          </Box>
          <ObjectList />
        </Box>
      </Box>
    </Box>
  );
}
